<!-- One control: a text box. No preset chips — a row ordered by what you used
     most moves position as you use it, so it has to be read every time, and
     `/` then Enter is the same two keys forever. -->
<div class="ai-adjust">
  <div class="ai-adjust-box">

    <!-- Slash menu. Opens upward from the box; Create style is row 0 always,
         and sticky so it keeps that position once a long list scrolls. -->
    <div class="ai-slash" *ngIf="slashOpen">
      <button type="button" class="ai-slash-item ai-slash-item--create"
        *ngIf="slashRows.length && slashRows[0].id === CREATE_ROW"
        [class.is-sel]="slashIndex === 0"
        (mouseenter)="slashIndex = 0"
        (mousedown)="$event.preventDefault()" (click)="chooseSlashRow(0)">
        <span class="ai-slash-name">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>
          {{ 'Create style' | translate }}
        </span>
        <span class="ai-slash-desc">{{ 'Save prompt as a reusable style' | translate }}</span>
      </button>

      <ng-container *ngFor="let row of slashRows; let i = index">
        <!-- A row is not one button: the pencil is its own control, and a
             button inside a button is invalid markup that screen readers and
             taps both resolve unpredictably. -->
        <div class="ai-slash-item" *ngIf="row.id !== CREATE_ROW"
          [class.is-sel]="slashIndex === i"
          (mouseenter)="slashIndex = i">
          <button type="button" class="ai-slash-main"
            (mousedown)="$event.preventDefault()" (click)="chooseSlashRow(i)">
            <span class="ai-slash-name">/{{ row.id }}</span>
            <span class="ai-slash-desc">{{ row.skill?.instruction }}</span>
          </button>
          <button type="button" class="ai-slash-edit" *ngIf="row.skill"
            [attr.aria-label]="'Edit style' | translate"
            (mousedown)="$event.preventDefault()" (click)="openEdit(row.skill)">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
          </button>
        </div>
      </ng-container>

      <div class="ai-slash-empty" *ngIf="slashRows.length === 1">
        {{ 'No matching styles found. Enter instructions or create a new style.' | translate }}
      </div>
    </div>

    <div class="ai-adjust-ask" *ngIf="!createOpen">
      <ion-input #adjustInput class="ai-adjust-input"
        [ngModel]="instruction"
        [ngModelOptions]="{ standalone: true }"
        (ngModelChange)="onInstructionInput($event)"
        [disabled]="loading"
        [placeholder]="'Describe adjustments or type / for saved styles...' | translate"
        (keydown)="onAdjustKeydown($event)">
      </ion-input>
      <button type="button" class="ai-adjust-send"
        [disabled]="!instruction.trim() || loading"
        [attr.aria-label]="'Apply' | translate"
        (mousedown)="$event.preventDefault()" (click)="submitInstruction()">
        <svg *ngIf="!loading" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
        <span class="ai-adjust-spin" *ngIf="loading"></span>
      </button>
    </div>

    <!-- Dedicated style editor. Replaces the ask bar when open. -->
    <div class="ai-create" *ngIf="createOpen">
      <div class="ai-create-header">
        <div class="ai-create-title">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
          {{ (editingId ? 'Edit Style' : 'New Style') | translate }}
        </div>
        <button type="button" class="ai-create-close" [attr.aria-label]="'Cancel' | translate"
          (mousedown)="$event.preventDefault()" (click)="closeCreate()">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>
        </button>
      </div>

      <div class="ai-create-body">
        <div class="ai-create-slug-wrap">
          <span class="ai-create-prefix">/</span>
          <input id="ai-style-name" class="ai-create-name" [(ngModel)]="createName"
            [ngModelOptions]="{ standalone: true }" placeholder="e.g. audit-standard" autocomplete="off"
            (keydown.enter)="$event.preventDefault()">
        </div>

        <textarea class="ai-create-text" [(ngModel)]="createInstruction"
          [ngModelOptions]="{ standalone: true }"
          rows="3"
          [placeholder]="'Define drafting guidelines, required standards, or terminology constraints...' | translate"></textarea>

        <div class="ai-create-msg" *ngIf="createError">{{ createError }}</div>
      </div>

      <div class="ai-create-btns">
        <button type="button" class="ai-create-btn ai-create-btn--danger" *ngIf="editingId"
          (mousedown)="$event.preventDefault()" (click)="deleteSkill()">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
          {{ 'Delete' | translate }}
        </button>
        <span class="ai-create-spacer"></span>
        <button type="button" class="ai-create-btn ai-create-btn--cancel" (mousedown)="$event.preventDefault()" (click)="closeCreate()">
          {{ 'Cancel' | translate }}
        </button>
        <button type="button" class="ai-create-btn ai-create-btn--primary" [disabled]="!canSaveSkill"
          (mousedown)="$event.preventDefault()" (click)="saveSkill()">
          {{ 'Save style' | translate }}
        </button>
      </div>
    </div>
  </div>

  <!-- One tab, one state at a time. Failure wins over success. -->
  <div class="ai-adjust-tab ai-adjust-tab--error" *ngIf="error && !loading">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M12 8v5M12 17h.01"/><circle cx="12" cy="12" r="9"/></svg>
    {{ error }}
  </div>

  <div class="ai-adjust-tab" *ngIf="savedSkillId && !error && !loading">
    {{ (savedWasEdit ? 'Updated' : 'Saved') | translate }}<span class="ai-adjust-slug">/{{ savedSkillId }}</span>
  </div>

  <div class="ai-adjust-tab" *ngIf="deletedSkillId && !error && !loading">
    {{ 'Deleted' | translate }}<span class="ai-adjust-slug">/{{ deletedSkillId }}</span>
  </div>

  <div class="ai-adjust-tab" *ngIf="applied && !savedSkillId && !deletedSkillId && !error && !loading">
    <ng-container *ngIf="appliedSkillId; else plainAdjusted">
      {{ 'Adjusted with' | translate }}<span class="ai-adjust-slug">/{{ appliedSkillId }}</span>
    </ng-container>
    <ng-template #plainAdjusted>{{ 'Adjusted' | translate }}</ng-template>
    <button type="button" class="ai-adjust-link" [disabled]="!canUndo"
      (mousedown)="$event.preventDefault()" (click)="undo.emit()">{{ 'Undo' | translate }}</button>
  </div>
</div>
